import { css } from 'foundations-theming/utils';
import { toPx } from '../../../utils/toPx';
import { SIZE } from '../../../constants/nonTokenisedValues';
export function remCalc(size) {
  return `${parseInt(size, 10)}px`;
}
export function emCalc(size) {
  return `${parseInt(size, 10) / parseInt('16px', 10)}em`;
}
export const toPercent = value => value == null ? undefined : `${value}%`;
export const percentToVW = width => {
  if (typeof width !== 'string') return width;
  return width.replace(/(\d+)%/g, '$1vw');
};
export const setUiTransition = (property, timing) => {
  const computedTiming = timing || '150ms';
  const computedProperty = property || 'all';
  return {
    transition: `${computedProperty} ${computedTiming} ease-out`
  };
};
export const getScreenReaderOnlyStyles = theme => {
  return {
    position: 'absolute',
    top: '0 !important',
    width: `${SIZE.SCREEN_READER_ONLY} !important`,
    height: `${SIZE.SCREEN_READER_ONLY} !important`,
    margin: `-${theme.space[25]} !important`,
    padding: '0 !important',
    overflow: 'hidden !important',
    clip: 'rect(0, 0, 0, 0) !important',
    border: '0 !important'
  };
};

// WARNING: This object is specifically used if we want to pass the styles as token props to a
// foundations-component and we don't have a DOM element to attach the styles to.
// Please use sparingly for now until we have a better solution.
export const screenReaderOnlyTokenProps = {
  position: 'absolute',
  insetBlockStart: '0',
  inlineSize: '1px',
  blockSize: '1px',
  margin: '-25',
  padding: '0',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  borderWidth: '0'
};

/**
 * @returns a mixin that sets the appropriate width and margins to match a page template with
 * `pageLayout="max-width"`.
 */
export const setTemplateMaxWidth = () => css(["margin-left:auto;margin-right:auto;max-width:", ";"], toPx(parseInt('1280px', 10) + parseInt('48px', 10) * 2));
export const setFontSmoothing = smoothing => {
  const computedSmoothing = smoothing || 'antialiased';

  // No `css` wrapper for this mixin, due to a bug in styled-components v3
  return `
    -webkit-font-smoothing: ${computedSmoothing};
    -moz-osx-font-smoothing: ${computedSmoothing === 'antialiased' ? 'grayscale' : computedSmoothing};
  `;
};
export const setObjectFontSmoothing = smoothing => {
  const computedSmoothing = smoothing || 'antialiased';
  return {
    webkitFontSmoothing: computedSmoothing,
    mozOsxFontSmoothing: computedSmoothing === 'antialiased' ? 'grayscale' : computedSmoothing
  };
};
export const setInputAppearance = theme => {
  return {
    display: 'block',
    blockSize: '40px',
    inlineSize: '100%',
    fontSize: theme.typography['body-200'].fontSize,
    lineHeight: '22px',
    textAlign: 'left',
    verticalAlign: 'middle',
    color: theme.color['text-core-default'],
    backgroundColor: theme.color['fill-surface-raised'],
    borderBlockWidth: theme.borderWidth['100'],
    borderInlineWidth: theme.borderWidth['100'],
    borderBlockStyle: 'solid',
    borderInlineStyle: 'solid',
    borderBlockColor: theme.color['border-core-default'],
    borderInlineColor: theme.color['border-core-default'],
    borderStartStartRadius: theme.borderRadius['100'],
    borderStartEndRadius: theme.borderRadius['100'],
    borderEndEndRadius: theme.borderRadius['100'],
    borderEndStartRadius: theme.borderRadius['100']
  };
};
export const VR_STATE_ATTRIBUTE = 'data-vr-state';
/**
 * Creates a combined selector that supports both CSS pseudo-classes and VR state attributes.
 * This enables visual regression testing by allowing states to be triggered via data attributes.
 *
 * @param selector - The CSS selector string containing pseudo-class states
 * @param state - The VR element state to replace pseudo-classes with
 * @returns A combined selector supporting both pseudo-classes and VR state attributes
 */
const createPseudoClassSelector = (selector, state) => {
  if (!selector.includes(`:${state}`)) {
    throw new Error(`The selector ${selector} does not contain the pseudo-class :${state}`);
  }
  const maybePrefix = selector.startsWith(':') ? '&' : '';
  const vrStateSelector = selector.replaceAll(`:${state}`, `${maybePrefix}[${VR_STATE_ATTRIBUTE}="${state}"]`);
  return `${selector},${vrStateSelector}`;
};

/**
 * Creates a selector that supports both :active pseudo-class and VR active state attribute.
 * This enables visual regression testing of active states by allowing them to be triggered
 * via data-vr-state="active" attribute.
 *
 * @param selector - The CSS selector string containing :active pseudo-class
 * @returns A combined selector supporting both :active pseudo-class and VR active state
 */
export const createActiveStateSelector = selector => {
  return createPseudoClassSelector(selector, 'active');
};

/**
 * Creates a selector that supports both :focus pseudo-class and VR focus state attribute.
 * This enables visual regression testing of focus states by allowing them to be triggered
 * via data-vr-state="focus" attribute.
 *
 * @param selector - The CSS selector string containing :focus pseudo-class
 * @returns A combined selector supporting both :focus pseudo-class and VR focus state
 */
export const createFocusStateSelector = selector => {
  return createPseudoClassSelector(selector, 'focus');
};

/**
 * Creates a selector that supports both :hover pseudo-class and VR hover state attribute.
 * This enables visual regression testing of hover states by allowing them to be triggered
 * via data-vr-state="hover" attribute.
 *
 * @param selector - The CSS selector string containing :hover pseudo-class
 * @returns A combined selector supporting both :hover pseudo-class and VR hover state
 */
export const createHoverStateSelector = selector => {
  return createPseudoClassSelector(selector, 'hover');
};